/**
 * Preference center.
 *
 * Route: /preferences/:token
 *
 * Token-gated. The token is signed by the worker (90-day TTL) and carries
 * the contact's email. We never put the email in the URL itself.
 *
 * The page lists every brand the contact has any relationship with, with
 * each brand's subscriptions first (what you get, in plain words, and how
 * often), then the reminders for things you've signed up for. A TSB marketing
 * subscription also offers "Less often: at most one a month" (stepped
 * unsubscribe Phase B, thread mct_768f1a14-6be; rules in
 * worker/src/send-frequency-core.ts). There's a per-brand "Unsubscribe from
 * all <Brand> emails" and a global "Unsubscribe from every email across all
 * brands" at the bottom. Design: docs/plans/ee-stepped-unsubscribe-and-partner-subscription.md §2 step 5.
 *
 * Safety net at the top: "If this is not your email, please ignore this
 * page" — links can be forwarded, and we don't want a forwarded-link click
 * to mutate the original recipient's preferences without their say.
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { BRAND_HOME_URL, SUPPORT_EMAIL } from './branding';
import { PolicyLinks } from './components/PolicyLinks';

const API = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

// ────────────────────────────────────────────────────────────────────────────
// Types (mirror worker/src/subscriptions.ts public shapes)
// ────────────────────────────────────────────────────────────────────────────

type SubscriptionStatus = 'subscribed' | 'unsubscribed' | 'pending';
type SubscriptionCategory = 'marketing' | 'transactional' | 'program';

interface PrefType {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  category: SubscriptionCategory;
  status: SubscriptionStatus;
  hasExplicitRow: boolean;
  defaultSubscribed: number;
  displayOrder: number;
  /** Absent on an older worker. */
  cadenceLabel?: string | null;
  lessOftenOffered?: boolean;
  frequency?: 'monthly' | null;
}

interface PrefBrandSection {
  brand: {
    id: string;
    slug: string;
    name: string;
    legalName: string | null;
    logoUrl: string | null;
  };
  types: PrefType[];
}

type SmsScopeState = 'none' | 'pending' | 'confirmed' | 'opted_out';

interface PrefSmsState {
  hasNumber: boolean;
  maskedPhone: string | null;
  transactional: SmsScopeState;
  marketing: SmsScopeState;
  stopped: boolean;
}

interface PrefReminder {
  kind: 'call' | 'program' | 'webinar';
  ref: string;
  label: string;
  nextAt: string | null;
  /** True = the member has turned reminders OFF for this item. */
  muted: boolean;
}

interface PrefDigest {
  mode: 'regional' | 'edition' | 'global' | 'off';
  edition: string | null;
  placed: { key: string; name: string };
  editions: Array<{ key: string; name: string }>;
}

interface PrefState {
  email: string;
  /** Null/absent when the member is not on the digest or an older worker; the row doesn't render. */
  digest?: PrefDigest | null;
  /** Absent on an older worker; the Reminders section simply doesn't render. */
  reminders?: PrefReminder[];
  brands: PrefBrandSection[];
  sms: PrefSmsState | null;
  hasAnyRelationship: boolean;
}

// The primary SMS number, for the "text START" re-subscribe hint. Kept in sync with
// the campaign's provisioned numbers (see CLAUDE.md → 10DLC campaign status).
const SMS_NUMBER_DISPLAY = '(415) 417-1792';

// ────────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────────

export default function PreferenceCenterPage() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<PrefState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [savedNotice, setSavedNotice] = useState(false);
  const [killBusy, setKillBusy] = useState(false);
  const [killSucceeded, setKillSucceeded] = useState(false);
  const [smsBusy, setSmsBusy] = useState(false);
  const [reminderBusy, setReminderBusy] = useState<string | null>(null);
  const [reminderError, setReminderError] = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/preferences/${encodeURIComponent(token)}`);
      if (res.status === 401) {
        setError('This preferences link has expired or is invalid. Reply to any email from us and we’ll send you a fresh one.');
        return;
      }
      if (!res.ok) {
        setError(`Couldn’t load your preferences (${res.status}). Please try again later.`);
        return;
      }
      const data = (await res.json()) as PrefState;
      setState(data);
    } catch (err) {
      setError(`Network error: ${(err as Error).message}`);
    }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  function setStatusLocal(typeId: string, status: SubscriptionStatus) {
    setState((s) => {
      if (!s) return s;
      return {
        ...s,
        brands: s.brands.map((b) => ({
          ...b,
          types: b.types.map((t) => t.id === typeId ? { ...t, status, hasExplicitRow: true } : t),
        })),
      };
    });
  }

  async function toggleType(typeId: string, currentStatus: SubscriptionStatus) {
    if (!token || !state) return;
    const next: SubscriptionStatus = currentStatus === 'subscribed' ? 'unsubscribed' : 'subscribed';
    setStatusLocal(typeId, next);
    setBusy(true);
    setSavedNotice(false);
    try {
      const res = await fetch(`${API}/api/preferences/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: [{ subscriptionTypeId: typeId, status: next }] }),
      });
      if (!res.ok) {
        // Roll back the optimistic update on failure
        setStatusLocal(typeId, currentStatus);
        return;
      }
      const data = (await res.json()) as PrefState;
      setState(data);
      setSavedNotice(true);
      setTimeout(() => setSavedNotice(false), 1800);
    } finally {
      setBusy(false);
    }
  }

  async function setFrequency(t: PrefType, frequency: 'monthly' | 'every') {
    if (!token) return;
    setBusy(true);
    setSavedNotice(false);
    try {
      const res = await fetch(`${API}/api/preferences/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: [{ subscriptionTypeId: t.id, frequency }] }),
      });
      if (!res.ok) return;
      // Show what the server saved, not what we clicked.
      setState((await res.json()) as PrefState);
      setSavedNotice(true);
      setTimeout(() => setSavedNotice(false), 1800);
    } finally {
      setBusy(false);
    }
  }

  async function unsubBrand(brandSection: PrefBrandSection) {
    if (!token) return;
    const subscribedTypes = brandSection.types.filter((t) => t.status === 'subscribed');
    if (subscribedTypes.length === 0) return;
    const ok = window.confirm(`Unsubscribe from all ${brandSection.brand.name} emails? This affects ${subscribedTypes.length} subscription${subscribedTypes.length === 1 ? '' : 's'}.`);
    if (!ok) return;
    setBusy(true);
    try {
      const updates = subscribedTypes.map((t) => ({ subscriptionTypeId: t.id, status: 'unsubscribed' as const }));
      const res = await fetch(`${API}/api/preferences/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (!res.ok) return;
      const data = (await res.json()) as PrefState;
      setState(data);
      setSavedNotice(true);
      setTimeout(() => setSavedNotice(false), 2000);
    } finally {
      setBusy(false);
    }
  }

  async function setReminderMuted(r: PrefReminder, muted: boolean) {
    if (!token) return;
    setReminderBusy(r.ref);
    setReminderError(false);
    setSavedNotice(false);
    try {
      const res = await fetch(`${API}/api/preferences/${encodeURIComponent(token)}/mutes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_kind: r.kind, item_ref: r.ref, muted }),
      });
      if (!res.ok) { setReminderError(true); return; }
      setState((await res.json()) as PrefState);
      setSavedNotice(true);
      setTimeout(() => setSavedNotice(false), 1800);
    } catch {
      setReminderError(true);
    } finally {
      setReminderBusy(null);
    }
  }

  async function setDigest(choice: { mode: PrefDigest['mode']; edition?: string }) {
    if (!token) return;
    setDigestBusy(true);
    setReminderError(false);
    setSavedNotice(false);
    try {
      const res = await fetch(`${API}/api/preferences/${encodeURIComponent(token)}/digest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(choice),
      });
      if (!res.ok) { setReminderError(true); return; }
      // Show what the server saved, not what we clicked.
      const { digest } = (await res.json()) as { digest: PrefDigest | null };
      setState((s) => (s ? { ...s, digest } : s));
      setSavedNotice(true);
      setTimeout(() => setSavedNotice(false), 1800);
    } catch {
      setReminderError(true);
    } finally {
      setDigestBusy(false);
    }
  }

  async function optOutSms(scope: 'transactional' | 'marketing' | 'all', label: string) {
    if (!token) return;
    const ok = window.confirm(`Turn off ${label}? You can opt back in any time from ${window.location.origin}/sms.`);
    if (!ok) return;
    setSmsBusy(true);
    setSavedNotice(false);
    try {
      const res = await fetch(`${API}/api/preferences/${encodeURIComponent(token)}/sms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scope, action: 'opt_out' }),
      });
      if (!res.ok) return;
      const data = (await res.json()) as PrefState;
      setState(data);
      setSavedNotice(true);
      setTimeout(() => setSavedNotice(false), 1800);
    } finally {
      setSmsBusy(false);
    }
  }

  async function killAll() {
    if (!token) return;
    const ok = window.confirm('Unsubscribe from EVERY email across ALL brands? This cannot be undone in one click — you’d need to opt back in to each one.');
    if (!ok) return;
    setKillBusy(true);
    try {
      const res = await fetch(`${API}/api/preferences/${encodeURIComponent(token)}/unsub-all`, {
        method: 'POST',
      });
      if (!res.ok) return;
      setKillSucceeded(true);
      await load();
    } finally {
      setKillBusy(false);
    }
  }

  if (error) {
    return (
      <div style={styles.shell}>
        <div style={styles.card}>
          <h1 style={styles.h1}>Preferences</h1>
          <p style={styles.body}>{error}</p>
          <p style={styles.muted}>
            Need help? Email <a href={`mailto:${SUPPORT_EMAIL}`} style={styles.link}>{SUPPORT_EMAIL}</a>.
          </p>
        </div>
      </div>
    );
  }

  if (!state) {
    return (
      <div style={styles.shell}>
        <div style={styles.card}>
          <p style={styles.muted}>Loading your preferences…</p>
        </div>
      </div>
    );
  }

  if (!state.hasAnyRelationship) {
    return (
      <div style={styles.shell}>
        <div style={styles.card}>
          <h1 style={styles.h1}>Preferences</h1>
          <p style={styles.body}>
            We don’t have a record of <strong>{state.email}</strong> on any of our lists. If this looks
            wrong, reply to any email you’ve received from us and we’ll sort it out.
          </p>
          <p style={styles.muted}>
            <a href={BRAND_HOME_URL} style={styles.link}>Back to home &rarr;</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.shell}>
      <div style={styles.card}>
        <header style={styles.header}>
          <h1 style={styles.h1}>Email preferences</h1>
          <p style={styles.muted}>Managing preferences for <strong>{state.email}</strong>.</p>
        </header>

        <div style={styles.safetyNote}>
          If this isn’t your email address, please ignore this page — the link was likely
          forwarded to you. Changes here only affect the address shown above.
        </div>

        {savedNotice && (
          <div style={styles.savedNotice} aria-live="polite">Preferences saved.</div>
        )}

        {killSucceeded && (
          <div style={styles.savedNotice} aria-live="polite">
            You’ve been unsubscribed from everything. We’re sorry to see you go.
          </div>
        )}

        {state.brands.map((section) => {
          const subs = section.types.filter((t) => t.category === 'marketing');
          const others = section.types.filter((t) => t.category !== 'marketing');
          return (
            <section key={section.brand.id} style={styles.section}>
              <div style={styles.sectionHeader}>
                {section.brand.logoUrl
                  ? <img src={section.brand.logoUrl} alt={section.brand.name} style={styles.logo} />
                  : null}
                <h2 style={styles.h2}>{section.brand.name}</h2>
              </div>

              {subs.length > 0 && (
                <>
                  <h3 style={styles.h3}>Your subscriptions</h3>
                  <ul style={styles.typeList}>
                    {subs.map((t) => (
                      <TypeRow key={t.id} t={t} busy={busy} onToggle={() => void toggleType(t.id, t.status)} onFrequency={(f) => void setFrequency(t, f)} />
                    ))}
                  </ul>
                </>
              )}

              {others.length > 0 && (
                <>
                  <h3 style={styles.h3}>Reminders for things you’ve signed up for</h3>
                  <ul style={styles.typeList}>
                    {others.map((t) => (
                      <TypeRow key={t.id} t={t} busy={busy} onToggle={() => void toggleType(t.id, t.status)} onFrequency={(f) => void setFrequency(t, f)} />
                    ))}
                  </ul>
                </>
              )}

              <button
                type="button"
                onClick={() => void unsubBrand(section)}
                disabled={busy || section.types.every((t) => t.status !== 'subscribed')}
                style={styles.brandUnsubBtn}
              >
                Unsubscribe from all {section.brand.name} emails
              </button>
            </section>
          );
        })}

        {state.digest && (
          <section style={styles.section} data-testid="digest-section">
            <div style={styles.sectionHeader}>
              <h2 style={styles.h2}>Your weekly calls roundup</h2>
            </div>
            <p style={styles.muted}>
              Every Monday, the calls you’re on this week. Turning this off never removes you from
              any call. Reminders for each call are separate, below.
            </p>
            {reminderError && (
              <div style={styles.safetyNote} role="alert">
                That didn’t save. Please try again, or email{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} style={styles.link}>{SUPPORT_EMAIL}</a>.
              </div>
            )}
            <fieldset disabled={digestBusy} style={{ border: 0, padding: 0, margin: 0 }}>
              {([
                { id: 'regional', label: `The calls in friendly hours where I am — ${state.digest.placed.name} (we think that’s you)`, mode: 'regional' as const },
                ...state.digest.editions.map((e) => ({ id: `edition:${e.key}`, label: e.name, mode: 'edition' as const, edition: e.key })),
                { id: 'global', label: 'Everything, globally', mode: 'global' as const },
                { id: 'off', label: 'Off', mode: 'off' as const },
              ] as Array<{ id: string; label: string; mode: PrefDigest['mode']; edition?: string }>).map((o) => {
                const current = state.digest!.mode === 'edition' ? `edition:${state.digest!.edition}` : state.digest!.mode;
                return (
                  <label key={o.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '6px 0', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="calls-digest"
                      checked={current === o.id}
                      onChange={() => void setDigest({ mode: o.mode, edition: o.edition })}
                    />
                    <span style={styles.typeDesc}>{o.label}</span>
                  </label>
                );
              })}
            </fieldset>
          </section>
        )}

        {state.reminders && state.reminders.length > 0 && (
          <section style={styles.section} data-testid="reminders-section">
            <div style={styles.sectionHeader}>
              <h2 style={styles.h2}>Reminders</h2>
            </div>
            <p style={styles.muted}>
              Turn off the reminder emails for anything you don’t need nudging about. Turning
              reminders off never removes you from anything: you still have access to join the
              call, to all the sessions of a program, and to a webinar you’ve registered for.
            </p>
            {reminderError && (
              <div style={styles.safetyNote} role="alert">
                That didn’t save. Please try again, or email{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} style={styles.link}>{SUPPORT_EMAIL}</a>.
              </div>
            )}
            <ul style={styles.typeList}>
              {state.reminders.map((r) => (
                <li key={`${r.kind}:${r.ref}`} style={{ ...styles.typeRow, opacity: r.muted ? 0.7 : 1 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                    <span style={styles.typeText}>
                      <strong style={styles.typeName}>{r.label}</strong>
                      <span style={styles.typeDesc}>
                        {r.nextAt
                          ? `${r.kind === 'call' ? 'Next call' : r.kind === 'program' ? 'Next session' : 'Starts'}: ${formatNextCall(r.nextAt)}`
                          : `No ${r.kind === 'call' ? 'call' : r.kind === 'program' ? 'session' : 'date'} scheduled right now`}
                      </span>
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: r.muted ? 'var(--color-muted, #5b5f7a)' : '#146c2e' }}>
                        {r.muted ? 'Reminders off' : 'Reminders on'}
                      </span>
                      <button
                        type="button"
                        onClick={() => void setReminderMuted(r, !r.muted)}
                        disabled={reminderBusy !== null}
                        style={{ ...styles.brandUnsubBtn, marginTop: 0 }}
                        aria-label={`${r.muted ? 'Turn reminders back on for' : 'Turn off reminders for'} ${r.label}`}
                      >
                        {r.muted ? 'Turn back on' : 'Turn off'}
                      </button>
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {state.sms?.hasNumber && (
          <section style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.h2}>Text messages</h2>
            </div>
            <p style={styles.muted}>Managing texts for <strong>{state.sms.maskedPhone}</strong>.</p>

            {state.sms.stopped ? (
              <div style={styles.safetyNote}>
                You’ve opted out of all texts from us (you replied STOP). To start receiving them
                again, text <strong>START</strong> to {SMS_NUMBER_DISPLAY}.
              </div>
            ) : (
              <>
                <ul style={styles.typeList}>
                  <SmsPrefRow
                    name="Session & event reminders"
                    desc="Reminders for calls, sessions, and events you’ve signed up for."
                    scopeState={state.sms.transactional}
                    busy={smsBusy}
                    onOptOut={() => void optOutSms('transactional', 'session & event reminders')}
                  />
                  <SmsPrefRow
                    name="Offers & announcements"
                    desc="Occasional promotional texts about programs and events."
                    scopeState={state.sms.marketing}
                    busy={smsBusy}
                    pendingHint="Awaiting confirmation — reply Y to the text we sent."
                    onOptOut={() => void optOutSms('marketing', 'offers & announcements')}
                  />
                </ul>
                <p style={styles.muted}>
                  Want to add a number or turn a text type back on?{' '}
                  <a href="/sms" style={styles.link}>Manage your texts &rarr;</a>
                </p>
              </>
            )}
          </section>
        )}

        <footer style={styles.footer}>
          <button
            type="button"
            onClick={() => void killAll()}
            disabled={killBusy || killSucceeded}
            style={styles.nuclearBtn}
          >
            {killBusy ? 'Working…' : killSucceeded ? 'Unsubscribed' : 'Unsubscribe from every email across all brands'}
          </button>
          <p style={styles.muted}>
            Need help? Email <a href={`mailto:${SUPPORT_EMAIL}`} style={styles.link}>{SUPPORT_EMAIL}</a>.
          </p>
          <div style={{ marginTop: 8 }}><PolicyLinks /></div>
        </footer>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// One subscription: what it is, how often, on/off, and (where offered) the
// "less often" choice. Labels match worker/src/send-frequency-core.ts.
// ────────────────────────────────────────────────────────────────────────────

const EVERY_ONE_LABEL = 'Every one';
const LESS_OFTEN_LABEL = 'Less often: at most one a month';

function TypeRow({
  t, busy, onToggle, onFrequency,
}: {
  t: PrefType;
  busy: boolean;
  onToggle: () => void;
  onFrequency: (f: 'monthly' | 'every') => void;
}) {
  const on = t.status === 'subscribed';
  const showFrequency = on && t.lessOftenOffered === true;
  return (
    <li style={styles.typeRow}>
      <label style={styles.toggleLabel}>
        <input
          type="checkbox"
          checked={on}
          disabled={busy}
          onChange={onToggle}
          style={styles.checkbox}
        />
        <span style={styles.typeText}>
          <strong style={styles.typeName}>{t.name}</strong>
          {t.description ? <span style={styles.typeDesc}>{t.description}</span> : null}
          {t.cadenceLabel ? <span style={styles.cadence}>How often: {t.cadenceLabel}</span> : null}
        </span>
      </label>
      {showFrequency && (
        <fieldset disabled={busy} style={styles.freqSet} data-testid={`frequency-${t.slug}`}>
          <legend style={styles.srOnly}>How often for {t.name}</legend>
          {([
            { value: 'every' as const, label: EVERY_ONE_LABEL },
            { value: 'monthly' as const, label: LESS_OFTEN_LABEL },
          ]).map((o) => (
            <label key={o.value} style={styles.freqOption}>
              <input
                type="radio"
                name={`freq-${t.id}`}
                checked={(t.frequency === 'monthly' ? 'monthly' : 'every') === o.value}
                onChange={() => onFrequency(o.value)}
              />
              <span>{o.label}</span>
            </label>
          ))}
        </fieldset>
      )}
    </li>
  );
}

/** "Wed, Oct 7, 12:00 PM EDT" in the viewer's own timezone. */
function formatNextCall(iso: string): string {
  const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  });
}

// ────────────────────────────────────────────────────────────────────────────
// SMS preference row
// ────────────────────────────────────────────────────────────────────────────

function SmsPrefRow({
  name, desc, scopeState, busy, onOptOut, pendingHint,
}: {
  name: string;
  desc: string;
  scopeState: SmsScopeState;
  busy: boolean;
  onOptOut: () => void;
  pendingHint?: string;
}) {
  const isOn = scopeState === 'confirmed';
  const isPending = scopeState === 'pending';
  const statusLabel = isOn ? 'On' : isPending ? 'Pending' : 'Off';
  const statusColor = isOn ? '#146c2e' : isPending ? '#8a6d00' : 'var(--color-muted, #5b5f7a)';
  // Opt-out is offered for anything not already off (confirmed or pending).
  const canOptOut = isOn || isPending;

  return (
    <li style={styles.typeRow}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <span style={styles.typeText}>
          <strong style={styles.typeName}>{name}</strong>
          <span style={styles.typeDesc}>{desc}</span>
          {isPending && pendingHint ? <span style={{ ...styles.typeDesc, color: '#8a6d00' }}>{pendingHint}</span> : null}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: statusColor }}>{statusLabel}</span>
          {canOptOut ? (
            <button type="button" onClick={onOptOut} disabled={busy} style={{ ...styles.brandUnsubBtn, marginTop: 0 }}>
              Turn off
            </button>
          ) : null}
        </span>
      </div>
    </li>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Styles (inline so the page works regardless of styles.css load state).
// ────────────────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  shell: {
    minHeight: '100vh',
    padding: '40px 20px 80px',
    background: 'var(--color-bg, #f8f6f3)',
    color: 'var(--color-text, #0a0a2e)',
    fontFamily: 'var(--font-body, "Poppins", system-ui, -apple-system, sans-serif)',
    display: 'flex',
    justifyContent: 'center',
  },
  card: {
    width: '100%',
    maxWidth: 720,
    background: 'var(--color-card, #ffffff)',
    borderRadius: 24,
    padding: '40px 32px',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)',
  },
  header: {
    marginBottom: 24,
  },
  h1: {
    fontSize: 28,
    fontWeight: 700,
    margin: 0,
    color: 'var(--color-text, #0a0a2e)',
  },
  h2: {
    fontSize: 20,
    fontWeight: 600,
    margin: 0,
    color: 'var(--color-text, #0a0a2e)',
  },
  body: {
    fontSize: 16,
    lineHeight: 1.55,
    color: 'var(--color-text, #0a0a2e)',
    margin: '12px 0',
  },
  muted: {
    fontSize: 13,
    color: 'var(--color-muted, #5b5f7a)',
    margin: '8px 0',
  },
  link: {
    color: 'var(--color-primary, #1a1aff)',
    textDecoration: 'underline',
  },
  safetyNote: {
    background: 'var(--color-tint, #f4f1ea)',
    border: '1px solid var(--color-border, #e5e0d2)',
    borderRadius: 12,
    padding: '12px 16px',
    fontSize: 13,
    lineHeight: 1.5,
    color: 'var(--color-muted, #5b5f7a)',
    margin: '20px 0 28px',
  },
  savedNotice: {
    background: 'var(--color-success-bg, #e8f7ed)',
    color: 'var(--color-success, #146c2e)',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 14,
    margin: '16px 0',
    fontWeight: 500,
  },
  section: {
    margin: '24px 0',
    paddingTop: 24,
    borderTop: '1px solid var(--color-border, #e5e0d2)',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  logo: {
    width: 32,
    height: 32,
    borderRadius: 8,
    objectFit: 'contain',
  },
  typeList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  typeRow: {
    padding: '10px 0',
    borderBottom: '1px solid var(--color-border-faint, #f0ece1)',
  },
  toggleLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    cursor: 'pointer',
  },
  checkbox: {
    marginTop: 4,
    width: 18,
    height: 18,
    cursor: 'pointer',
  },
  typeText: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  typeName: {
    fontSize: 15,
    fontWeight: 600,
    color: 'var(--color-text, #0a0a2e)',
  },
  typeDesc: {
    fontSize: 13,
    color: 'var(--color-muted, #5b5f7a)',
    lineHeight: 1.4,
  },
  cadence: {
    fontSize: 12,
    color: 'var(--color-muted, #8a8da3)',
    lineHeight: 1.4,
  },
  h3: {
    fontSize: 14,
    fontWeight: 600,
    margin: '8px 0 4px',
    color: 'var(--color-muted, #5b5f7a)',
  },
  freqSet: {
    border: 0,
    padding: '6px 0 0 30px',
    margin: 0,
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px 18px',
  },
  freqOption: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    cursor: 'pointer',
  },
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
  },
  brandUnsubBtn: {
    marginTop: 16,
    padding: '8px 14px',
    background: 'transparent',
    color: 'var(--color-muted, #5b5f7a)',
    border: '1px solid var(--color-border, #e5e0d2)',
    borderRadius: 8,
    fontSize: 13,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  footer: {
    marginTop: 40,
    paddingTop: 24,
    borderTop: '1px solid var(--color-border, #e5e0d2)',
  },
  nuclearBtn: {
    width: '100%',
    padding: '14px 16px',
    background: 'transparent',
    color: '#a1212c',
    border: '1px solid #e7c2c5',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
    marginBottom: 16,
  },
};
